import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Badge,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  PlayArrow as PlayIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Message as MessageIcon,
  Webhook as WebhookIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/environment';

interface WhatsAppInstance {
  id: string;
  name: string;
  instance_name: string;
  phone_number: string;
  status: 'active' | 'inactive' | 'blocked' | 'maintenance' | 'connected' | 'disconnected' | 'connecting' | 'error';
  is_primary: boolean;
  priority: number;
  daily_limit: number;
  daily_sent: number;
  is_connected: boolean;
  last_activity?: string;
  instance_type?: 'onboarding' | 'recovery' | 'both';
  provider?: 'evolution' | 'z-api'; // Tipo de provider
  z_api_instance_id?: string; // Para instâncias Z-API
}

interface AutoMessage {
  id: string;
  name: string;
  description?: string;
  crm_stage_id: string;
  crm_pipeline_id: string;
  message_template: string;
  message_type: 'text' | 'image' | 'video' | 'document' | 'audio';
  media_url?: string;
  media_caption?: string;
  is_active: boolean;
  delay_minutes: number;
  send_conditions: Record<string, any>;
  variables: Record<string, any>;
  crm_stages?: {
    name: string;
    stage_order: number;
  };
  crm_pipelines?: {
    name: string;
    color: string;
  };
}

interface CrmPipeline {
  id: string;
  name: string;
  color: string;
  stages: CrmStage[];
}

interface CrmStage {
  id: string;
  name: string;
  color: string;
  stage_order: number;
  pipeline_id: string;
}

interface SystemConfig {
  auto_messages_enabled: boolean;
  max_daily_messages_per_contact: number;
  message_retry_attempts: number;
  distribution_algorithm: 'random' | 'round_robin' | 'priority' | 'load_balanced';
  respect_business_hours: boolean;
  business_hours_start: string;
  business_hours_end: string;
  timezone: string;
  queue_processing_interval: number;
  max_queue_size: number;
}

interface QueueItem {
  id: string;
  contact_id: string;
  whatsapp_instance_id?: string;
  auto_message_id: string;
  message_content: string;
  message_type: string;
  status: 'pending' | 'sending' | 'sent' | 'failed' | 'cancelled';
  priority: number;
  scheduled_at: string;
  sent_at?: string;
  failed_at?: string;
  error_message?: string;
  contacts?: {
    name: string;
    custom_fields?: any;
  };
  whatsapp_instances?: {
    name: string;
    phone_number: string;
  };
  whatsapp_auto_messages?: {
    name: string;
  };
}

interface Stats {
  instances: {
    total: number;
    connected: number;
    available: number;
  };
  queue: Record<string, number>;
  sent: Record<string, number>;
  total_sent_today: number;
}

const WhatsAppAuto: React.FC = () => {
  const theme = useTheme();
  const [selectedProvider, setSelectedProvider] = useState<'evolution' | 'z-api'>('evolution');
  const [activeTab, setActiveTab] = useState(0);
  const [instances, setInstances] = useState<WhatsAppInstance[]>([]);
  const [autoMessages, setAutoMessages] = useState<AutoMessage[]>([]);
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isConfiguringWebhooks, setIsConfiguringWebhooks] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  
  // Estados para configurações IA
  const [n8nWebhookEnabled, setN8nWebhookEnabled] = useState(false);
  const [n8nWebhookUrl, setN8nWebhookUrl] = useState('');
  const [n8nWebhookTimeout, setN8nWebhookTimeout] = useState(30000);
  const [n8nAiResponseEnabled, setN8nAiResponseEnabled] = useState(true);
  const [isTestingWebhook, setIsTestingWebhook] = useState(false);


  // Novos estados para funcionalidades avançadas
  const [crmPipelines, setCrmPipelines] = useState<CrmPipeline[]>([]);
  const [crmStages, setCrmStages] = useState<CrmStage[]>([]);
  const [systemConfig, setSystemConfig] = useState<SystemConfig | null>(null);
  const [messagePreview, setMessagePreview] = useState<string>('');
  const [testMessage, setTestMessage] = useState({ phone: '', content: '' });

  // Estados para diálogos
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [createInstanceDialog, setCreateInstanceDialog] = useState(false);
  const [editInstanceDialog, setEditInstanceDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingInstance, setEditingInstance] = useState<WhatsAppInstance | null>(null);
  
  // Novos diálogos
  const [messageConfigDialog, setMessageConfigDialog] = useState(false);
  const [templateEditorDialog, setTemplateEditorDialog] = useState(false);
  const [conditionsDialog, setConditionsDialog] = useState(false);
  const [testMessageDialog, setTestMessageDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);


  const [messageForm, setMessageForm] = useState({
    name: '',
    description: '',
    crm_stage_id: '',
    crm_pipeline_id: '',
    message_template: '',
    message_type: 'text' as 'text' | 'image' | 'video' | 'document' | 'audio',
    media_url: '',
    media_caption: '',
    delay_minutes: 0,
    is_active: true,
    send_conditions: {
      has_whatsapp: true,
      source: [] as string[],
      time_of_day: 'business_hours',
      max_daily_per_contact: 3,
      exclude_weekends: false,
      min_delay_between_messages: 60
    },
    variables: {
      contact_name: 'Nome do contato',
      stage_name: 'Nome do estágio',
      pipeline_name: 'Nome do pipeline',
      date: 'Data atual',
      time: 'Hora atual'
    }
  });

  // Estado para criar nova instância Evolution API
  const [newInstanceForm, setNewInstanceForm] = useState({
    name: '',
    instance_name: '',
    daily_limit: 100,
    priority: 1,
    instance_type: 'both' as 'onboarding' | 'recovery' | 'both'
  });

  // Estado para criar nova instância Z-API
  const [newZApiInstanceForm, setNewZApiInstanceForm] = useState({
    name: '',
    instance_id: '',
    instance_token: '',
    client_token: '',
    daily_limit: 100,
    is_primary: false
  });

  // Estado para editar instância existente
  const [editInstanceForm, setEditInstanceForm] = useState({
    name: '',
    daily_limit: 100,
    priority: 1,
    instance_type: 'both' as 'onboarding' | 'recovery' | 'both'
  });

  // Carregar dados iniciais
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const API_BASE_URL = getApiUrl();
      
      // Buscar instâncias de ambos os providers
      let allInstances: WhatsAppInstance[] = [];
      
      // Buscar instâncias Evolution API
      try {
        const evolutionRes = await axios.get(`${API_BASE_URL}/whatsapp-auto/instances`);
        const evolutionInstances = (evolutionRes.data.data || []).map((inst: any) => ({
          ...inst,
          provider: 'evolution' as const
        }));
        allInstances = [...allInstances, ...evolutionInstances];
      } catch (err) {
        console.warn('Erro ao buscar instâncias Evolution:', err);
      }

      // Buscar instâncias Z-API
      try {
        const zApiRes = await axios.get(`${API_BASE_URL}/z-api-instances`);
        const zApiInstances = (zApiRes.data.data || []).map((inst: any) => ({
          id: inst.id,
          name: inst.name,
          instance_name: inst.instance_id || inst.instance_name || '',
          phone_number: inst.phone_number || '',
          status: inst.status || (inst.is_connected ? 'active' : 'inactive'),
          is_primary: inst.is_primary || false,
          priority: inst.priority || 1,
          daily_limit: inst.daily_limit || 100,
          daily_sent: inst.daily_sent || 0,
          is_connected: inst.is_connected || inst.status === 'active' || inst.status === 'connected',
          last_activity: inst.last_activity,
          instance_type: inst.instance_type || 'both',
          provider: 'z-api' as const,
          z_api_instance_id: inst.instance_id
        }));
        allInstances = [...allInstances, ...zApiInstances];
      } catch (err) {
        console.warn('Erro ao buscar instâncias Z-API:', err);
      }

      // Filtrar por provider selecionado (se não for 'all')
      const filteredInstances = allInstances.filter(inst => inst.provider === selectedProvider);
      
      setInstances(filteredInstances);

      // Carregar outros dados
      const [messagesRes, queueRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/whatsapp-auto/messages`),
        axios.get(`${API_BASE_URL}/whatsapp-auto/queue`),
        axios.get(`${API_BASE_URL}/whatsapp-auto/stats`)
      ]);

      setAutoMessages(messagesRes.data.data || []);
      setQueueItems(queueRes.data.data || []);
      setStats(statsRes.data.data || null);

      // Carregar dados do CRM
      await loadCrmData();

      // Carregar configurações do sistema
      await loadSystemConfig();
    } catch (err) {
      setError('Erro ao carregar dados');
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados do CRM
  const loadCrmData = async () => {
    try {
      const API_BASE_URL = getApiUrl();
      // Carregar pipelines
      const pipelinesResponse = await axios.get(`${API_BASE_URL}/crm/pipelines`);
      setCrmPipelines(pipelinesResponse.data.data || []);

      // Carregar estágios
      const stagesResponse = await axios.get(`${API_BASE_URL}/crm/stages`);
      setCrmStages(stagesResponse.data.data || []);
    } catch (err) {
      console.error('Erro ao carregar dados do CRM:', err);
    }
  };

  // Carregar configurações do sistema
  const loadSystemConfig = async () => {
    try {
      const API_BASE_URL = getApiUrl();
      console.log('🔄 Carregando configurações do sistema...');
      const response = await axios.get(`${API_BASE_URL}/whatsapp-auto/settings`);
      const settings = response.data.data || [];
      console.log('📥 Configurações recebidas do servidor:', settings);
      console.log('📊 Tipo de settings:', typeof settings, Array.isArray(settings));
      
      // Verificar se settings é um objeto ou array
      let n8nSettings: any = {};
      
      if (Array.isArray(settings)) {
        // Se é array, usar reduce como antes
        console.log('✅ Settings é um array, usando reduce');
        setSystemConfig(settings as any);
        n8nSettings = settings.reduce((acc: any, setting: any) => {
          acc[setting.setting_key] = setting.setting_value;
          return acc;
        }, {});
      } else if (typeof settings === 'object' && settings !== null) {
        // Se é objeto direto, usar como está
        console.log('✅ Settings é um objeto, usando diretamente');
        setSystemConfig([settings] as any);
        n8nSettings = settings;
      } else {
        console.warn('⚠️ Settings em formato inesperado:', settings);
        return;
      }
      
      console.log('🔧 Configurações IA processadas:', n8nSettings);
      
      setN8nWebhookEnabled(n8nSettings.n8n_webhook_enabled === 'true' || n8nSettings.n8n_webhook_enabled === true);
      setN8nWebhookUrl(n8nSettings.n8n_webhook_url || '');
      setN8nWebhookTimeout(parseInt(n8nSettings.n8n_webhook_timeout) || 30000);
      setN8nAiResponseEnabled(n8nSettings.n8n_ai_response_enabled === 'true' || n8nSettings.n8n_ai_response_enabled === true);
      
      console.log('✅ Estados configurados:', {
        enabled: n8nSettings.n8n_webhook_enabled === 'true' || n8nSettings.n8n_webhook_enabled === true,
        url: n8nSettings.n8n_webhook_url || '',
        timeout: parseInt(n8nSettings.n8n_webhook_timeout) || 30000,
        aiResponse: n8nSettings.n8n_ai_response_enabled === 'true' || n8nSettings.n8n_ai_response_enabled === true
      });
    } catch (err) {
      console.error('❌ Erro ao carregar configurações:', err);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };


  // Salvar configurações IA
  const saveN8nSettings = async () => {
    try {
      const API_BASE_URL = getApiUrl();
      const settings = {
        n8n_webhook_enabled: n8nWebhookEnabled,
        n8n_webhook_url: n8nWebhookUrl,
        n8n_webhook_timeout: n8nWebhookTimeout,
        n8n_ai_response_enabled: n8nAiResponseEnabled
      };

      console.log('💾 Salvando configurações IA:', settings);
      const response = await axios.put(`${API_BASE_URL}/whatsapp-auto/settings`, settings);
      console.log('✅ Resposta do servidor:', response.data);
      
      showSnackbar('Configurações IA salvas com sucesso!', 'success');
      
      // Recarregar configurações para confirmar que foram salvas
      await loadSystemConfig();
      console.log('🔄 Configurações recarregadas');
    } catch (err: any) {
      console.error('❌ Erro ao salvar configurações IA:', err);
      showSnackbar(err.response?.data?.error || 'Erro ao salvar configurações IA', 'error');
    }
  };

  // Testar webhook IA
  const testN8nWebhook = async () => {
    if (!n8nWebhookUrl) {
      showSnackbar('URL do webhook é obrigatória', 'error');
      return;
    }

    setIsTestingWebhook(true);
    try {
      const API_BASE_URL = getApiUrl();
      const response = await axios.post(`${API_BASE_URL}/whatsapp-auto/n8n-webhook/test`, {
        webhook_url: n8nWebhookUrl
      });

      if (response.data.success) {
        showSnackbar('Webhook IA testado com sucesso!', 'success');
      } else {
        showSnackbar('Erro ao testar webhook IA', 'error');
      }
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || 'Erro ao testar webhook IA', 'error');
    } finally {
      setIsTestingWebhook(false);
    }
  };

  // Funções para gerenciar mensagens automáticas
  const handleCreateMessage = async () => {
    try {
      const API_BASE_URL = getApiUrl();
      await axios.post(`${API_BASE_URL}/whatsapp-auto/messages`, messageForm);
      showSnackbar('Mensagem automática criada com sucesso!');
      setMessageDialogOpen(false);
      resetMessageForm();
      loadData();
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || 'Erro ao criar mensagem automática', 'error');
    }
  };

  const handleUpdateMessage = async () => {
    try {
      const API_BASE_URL = getApiUrl();
      await axios.put(`${API_BASE_URL}/whatsapp-auto/messages/${selectedItem.id}`, messageForm);
      showSnackbar('Mensagem automática atualizada com sucesso!');
      setMessageDialogOpen(false);
      resetMessageForm();
      loadData();
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || 'Erro ao atualizar mensagem automática', 'error');
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta mensagem automática?')) {
      return;
    }

    try {
      const API_BASE_URL = getApiUrl();
      await axios.delete(`${API_BASE_URL}/whatsapp-auto/messages/${messageId}`);
      showSnackbar('Mensagem automática excluída com sucesso!');
      loadData();
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || 'Erro ao excluir mensagem automática', 'error');
    }
  };

  const resetMessageForm = () => {
    setMessageForm({
      name: '',
      description: '',
      crm_stage_id: '',
      crm_pipeline_id: '',
      message_template: '',
      message_type: 'text',
      media_url: '',
      media_caption: '',
      delay_minutes: 0,
      is_active: true,
      send_conditions: {
        has_whatsapp: true,
        source: [],
        time_of_day: 'business_hours',
        max_daily_per_contact: 3,
        exclude_weekends: false,
        min_delay_between_messages: 60
      },
      variables: {
        contact_name: 'Nome do contato',
        stage_name: 'Nome do estágio',
        pipeline_name: 'Nome do pipeline',
        date: 'Data atual',
        time: 'Hora atual'
      }
    });
  };

  // Funções para preview e teste
  const generateMessagePreview = () => {
    let preview = messageForm.message_template;
    
    // Substituir variáveis
    preview = preview.replace(/\{\{contact_name\}\}/g, 'João Silva');
    preview = preview.replace(/\{\{stage_name\}\}/g, 'Novo Lead');
    preview = preview.replace(/\{\{pipeline_name\}\}/g, 'Vendas');
    preview = preview.replace(/\{\{date\}\}/g, new Date().toLocaleDateString('pt-BR'));
    preview = preview.replace(/\{\{time\}\}/g, new Date().toLocaleTimeString('pt-BR'));
    
    setMessagePreview(preview);
    setPreviewDialog(true);
  };

  const handleTestMessage = async () => {
    try {
      const API_BASE_URL = getApiUrl();
      await axios.post(`${API_BASE_URL}/whatsapp-auto/test-message`, {
        phone: testMessage.phone,
        content: testMessage.content
      });
      showSnackbar('Mensagem de teste enviada com sucesso!');
      setTestMessageDialog(false);
      setTestMessage({ phone: '', content: '' });
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || 'Erro ao enviar mensagem de teste', 'error');
    }
  };

  const handleProcessQueue = async () => {
    try {
      const API_BASE_URL = getApiUrl();
      await axios.post(`${API_BASE_URL}/whatsapp-auto/queue/process`);
      showSnackbar('Fila processada com sucesso!');
      loadData();
    } catch (err) {
      showSnackbar('Erro ao processar fila', 'error');
      console.error('Erro ao processar fila:', err);
    }
  };

  // Função para criar nova instância Evolution API
  const handleCreateEvolutionInstance = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = getApiUrl();
      const response = await axios.post(`${API_BASE_URL}/whatsapp-auto/instances/evolution`, {
        ...newInstanceForm,
        is_connected: false,
        status: 'active'
      });
      
      if (response.data.success) {
        showSnackbar('Instância Evolution API criada com sucesso!', 'success');
        setCreateInstanceDialog(false);
        setNewInstanceForm({
          name: '',
          instance_name: '',
          daily_limit: 100,
          priority: 1,
          instance_type: 'both'
        });
        loadData(); // Recarregar dados
      } else {
        showSnackbar('Erro ao criar instância Evolution API', 'error');
      }
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || 'Erro ao criar instância Evolution API', 'error');
      console.error('Erro ao criar instância Evolution API:', err);
    } finally {
      setLoading(false);
    }
  };

  // Função para criar nova instância Z-API
  const handleCreateZApiInstance = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = getApiUrl();
      const response = await axios.post(`${API_BASE_URL}/z-api-instances`, {
        name: newZApiInstanceForm.name,
        instance_id: newZApiInstanceForm.instance_id,
        token: newZApiInstanceForm.instance_token, // Backend espera 'token', não 'instance_token'
        client_token: newZApiInstanceForm.client_token,
        daily_limit: newZApiInstanceForm.daily_limit,
        is_primary: newZApiInstanceForm.is_primary
      });
      
      if (response.data.success) {
        showSnackbar('Instância Z-API criada com sucesso!', 'success');
        setCreateInstanceDialog(false);
        setNewZApiInstanceForm({
          name: '',
          instance_id: '',
          instance_token: '',
          client_token: '',
          daily_limit: 100,
          is_primary: false
        });
        setSelectedProvider('evolution'); // Reset para Evolution
        loadData(); // Recarregar dados
      } else {
        showSnackbar('Erro ao criar instância Z-API', 'error');
      }
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || 'Erro ao criar instância Z-API', 'error');
      console.error('Erro ao criar instância Z-API:', err);
    } finally {
      setLoading(false);
    }
  };

  // Função para editar instância
  const handleEditInstance = (instance: WhatsAppInstance) => {
    setEditingInstance(instance);
    setEditInstanceForm({
      name: instance.name,
      daily_limit: instance.daily_limit,
      priority: instance.priority,
      instance_type: (instance.instance_type || 'both') as 'onboarding' | 'recovery' | 'both'
    });
    setEditInstanceDialog(true);
  };

  // Função para atualizar instância
  const handleUpdateInstance = async () => {
    if (!editingInstance) return;

    try {
      setLoading(true);
      const API_BASE_URL = getApiUrl();
      const response = await axios.put(`${API_BASE_URL}/whatsapp-auto/instances/${editingInstance.id}`, {
        name: editInstanceForm.name,
        daily_limit: editInstanceForm.daily_limit,
        priority: editInstanceForm.priority,
        instance_type: editInstanceForm.instance_type
      });
      
      if (response.data.success) {
        showSnackbar('Instância atualizada com sucesso!', 'success');
        setEditInstanceDialog(false);
        setEditingInstance(null);
        loadData(); // Recarregar dados
      } else {
        showSnackbar('Erro ao atualizar instância', 'error');
      }
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || 'Erro ao atualizar instância', 'error');
      console.error('Erro ao atualizar instância:', err);
    } finally {
      setLoading(false);
    }
  };

  // Função para excluir instância
  const handleDeleteInstance = async (instanceId: string, instanceName: string) => {
    if (!window.confirm(`Tem certeza que deseja excluir a instância "${instanceName}"? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      setLoading(true);
      const API_BASE_URL = getApiUrl();
      const response = await axios.delete(`${API_BASE_URL}/whatsapp-auto/instances/${instanceId}`);
      
      if (response.data.success) {
        showSnackbar('Instância excluída com sucesso!', 'success');
        loadData(); // Recarregar dados
      } else {
        showSnackbar('Erro ao excluir instância', 'error');
      }
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || 'Erro ao excluir instância', 'error');
      console.error('Erro ao excluir instância:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAllInstances = async () => {
    setIsSyncing(true);
    try {
      const API_BASE_URL = getApiUrl();
      const response = await axios.post(`${API_BASE_URL}/whatsapp-auto/instances/sync-all-status`);
      
      if (response.data.success) {
        const { total, updated, results } = response.data.data;
        showSnackbar(`Sincronização concluída: ${updated} de ${total} instâncias atualizadas`, 'success');
        
        // Recarregar dados para mostrar status atualizado
        loadData();
        
        // Mostrar detalhes da sincronização
        console.log('Resultados da sincronização:', results);
      } else {
        showSnackbar(response.data.error || 'Erro na sincronização', 'error');
      }
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || 'Erro ao sincronizar instâncias', 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleConfigureWebhooks = async () => {
    setIsConfiguringWebhooks(true);
    try {
      const API_BASE_URL = getApiUrl();
      const response = await axios.post(`${API_BASE_URL}/whatsapp-auto/instances/configure-webhooks`);
      
      if (response.data.success) {
        const { success, failed, results } = response.data.data;
        showSnackbar(`Webhooks configurados: ${success} sucessos, ${failed} falhas`, 'success');
        
        // Mostrar detalhes da configuração
        console.log('Resultados da configuração de webhooks:', results);
      } else {
        showSnackbar(response.data.error || 'Erro na configuração de webhooks', 'error');
      }
    } catch (err: any) {
      showSnackbar(err.response?.data?.error || 'Erro ao configurar webhooks', 'error');
    } finally {
      setIsConfiguringWebhooks(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'blocked': return 'error';
      case 'maintenance': return 'warning';
      case 'pending': return 'info';
      case 'sending': return 'warning';
      case 'sent': return 'success';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
      case 'sent':
        return <CheckCircleIcon />;
      case 'failed':
      case 'blocked':
        return <ErrorIcon />;
      case 'pending':
      case 'sending':
        return <WarningIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      backgroundColor: 'transparent', 
      minHeight: '100vh',
      color: 'text.primary',
      position: 'relative',
      zIndex: 1,
    }}>

      {/* Header com título */}
      <Box sx={{ 
        mb: { xs: 2, md: 3 }, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' },
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, md: 0 },
        padding: { xs: '16px', md: '20px 24px' },
        position: 'relative',
        zIndex: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Box>
            <Typography variant="h4" sx={{ 
              fontWeight: 'bold', 
              color: '#000000',
              fontSize: { xs: '1.5rem', md: '2.125rem' },
              mb: 0.5,
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <WhatsAppIcon sx={{ fontSize: { xs: 24, md: 32 }, color: '#3b82f6' }} />
              WhatsApp Automático
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontSize: { xs: '0.75rem', md: '0.875rem' },
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            }}>
              Sistema inteligente de distribuição automática de mensagens WhatsApp
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
            sx={{
              background: 'rgba(59, 130, 246, 0.08)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              color: '#475569',
              '&:hover': {
                background: 'rgba(59, 130, 246, 0.12)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
              }
            }}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<PlayIcon />}
            onClick={handleProcessQueue}
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
              }
            }}
          >
            Processar Fila
          </Button>
        </Box>
      </Box>

      {/* Estatísticas */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className="glass-blue-card" sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)'
                  }}>
                    <WhatsAppIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#000000' }}>
                      {stats?.instances?.total ?? 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Instâncias WhatsApp
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className="glass-blue-card" sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                  }}>
                    <CheckCircleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#000000' }}>
                      {stats?.instances?.connected ?? 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Conectadas
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className="glass-blue-card" sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                    boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)'
                  }}>
                    <MessageIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#000000' }}>
                      {stats?.total_sent_today ?? 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enviadas Hoje
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className="glass-blue-card" sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)'
                  }}>
                    <ScheduleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#000000' }}>
                      {queueItems.filter(item => item.status === 'pending').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Na Fila
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper className="glass-blue-card" sx={{ 
        mb: 3,
      }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: '#64748b',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
              '&.Mui-selected': {
                color: '#3b82f6',
                fontWeight: 700,
              },
              '&:hover': {
                color: '#475569',
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#3b82f6',
              height: 3,
              borderRadius: '2px 2px 0 0',
            }
          }}
        >
          <Tab label="Instâncias" />
          <Tab label="Webhook IA" />
        </Tabs>
      </Paper>

      {/* Tab: Instâncias */}
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#000000' }}>
              Instâncias WhatsApp
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>Provedor</InputLabel>
              <Select
                value={selectedProvider}
                onChange={(e) => {
                  setSelectedProvider(e.target.value as 'evolution' | 'z-api');
                  loadData();
                }}
                label="Provedor"
                sx={{
                  color: '#000000',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(255, 255, 255, 0.8)',
                  },
                }}
              >
                <MenuItem value="evolution">Evolution API</MenuItem>
                <MenuItem value="z-api">Z-API</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setCreateInstanceDialog(true)}
              sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#000000',
                fontWeight: 600,
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                }
              }}
            >
              Conectar Novo Número
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleSyncAllInstances}
              disabled={isSyncing}
              sx={{
                border: '2px solid rgba(255, 255, 255, 0.3)',
                color: '#000000',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                backdropFilter: 'blur(10px)',
                background: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid rgba(255, 255, 255, 0.5)',
                },
                '&:disabled': {
                  opacity: 0.5,
                  color: '#94a3b8',
                }
              }}
            >
              {isSyncing ? 'Sincronizando...' : 'Sincronizar Instâncias'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<WebhookIcon />}
              onClick={handleConfigureWebhooks}
              disabled={isConfiguringWebhooks}
              sx={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 2,
                color: '#000000',
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
                py: 1.5,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
                },
                '&:disabled': {
                  opacity: 0.5,
                  color: '#94a3b8',
                }
              }}
            >
              {isConfiguringWebhooks ? 'Configurando...' : 'Configurar Webhooks'}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {instances.map((instance) => (
            <Grid item xs={12} sm={6} md={4} key={instance.id}>
              <Card className="glass-blue-card" sx={{
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-3px) scale(1.02)',
                },
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#000000' }}>
                      {instance.name}
                    </Typography>
                    <Chip
                      label={instance.status}
                      color={getStatusColor(instance.status) as any}
                      size="small"
                      icon={getStatusIcon(instance.status)}
                      sx={{
                        background: 'rgba(59, 130, 246, 0.08)',
                        border: '1px solid rgba(59, 130, 246, 0.2)',
                        color: '#000000',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WhatsAppIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                    {instance.phone_number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Instância: {instance.instance_name}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#000000', fontWeight: 600 }}>
                      {instance.daily_sent}/{instance.daily_limit} mensagens
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton 
                        size="small"
                        onClick={() => handleEditInstance(instance)}
                        sx={{
                          color: '#64748b',
                          '&:hover': {
                            color: '#3b82f6',
                            background: 'rgba(59, 130, 246, 0.1)',
                          }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteInstance(instance.id, instance.name)}
                        disabled={loading}
                        sx={{
                          color: '#64748b',
                          '&:hover': {
                            color: '#ef4444',
                            background: 'rgba(239, 68, 68, 0.1)',
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(instance.daily_sent / instance.daily_limit) * 100}
                    sx={{ 
                      mt: 1,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        borderRadius: 3,
                      }
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Tab: Webhook IA */}
      <TabPanel value={activeTab} index={1}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, color: theme.palette.primary.main }}>
            Configurações de Webhook IA
          </Typography>
          
          <Card className="glass-blue-card" sx={{ 
            mb: 3,
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: '#000000' }}>
                Configuração do Webhook
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={n8nWebhookEnabled}
                    onChange={(e) => setN8nWebhookEnabled(e.target.checked)}
                  />
                }
                label="Habilitar Webhook IA"
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="URL do Webhook IA"
                value={n8nWebhookUrl}
                onChange={(e) => setN8nWebhookUrl(e.target.value)}
                placeholder="https://sua-ia.com/webhook/whatsapp-ai"
                disabled={!n8nWebhookEnabled}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(59, 130, 246, 0.08)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '8px',
                    color: '#475569',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(59, 130, 246, 0.4)',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                    },
                    '&.Mui-focused': {
                      borderColor: 'rgba(59, 130, 246, 0.6)',
                      boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#475569',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': {
                      color: 'rgba(59, 130, 246, 0.8)',
                    },
                  },
                }}
              />
              
              <TextField
                fullWidth
                label="Timeout (ms)"
                type="number"
                value={n8nWebhookTimeout}
                onChange={(e) => setN8nWebhookTimeout(parseInt(e.target.value))}
                disabled={!n8nWebhookEnabled}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(59, 130, 246, 0.08)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    borderRadius: '8px',
                    color: '#475569',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(59, 130, 246, 0.4)',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                    },
                    '&.Mui-focused': {
                      borderColor: 'rgba(59, 130, 246, 0.6)',
                      boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#475569',
                      '&::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': {
                      color: 'rgba(59, 130, 246, 0.8)',
                    },
                  },
                }}
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={n8nAiResponseEnabled}
                    onChange={(e) => setN8nAiResponseEnabled(e.target.checked)}
                    disabled={!n8nWebhookEnabled}
                  />
                }
                label="Habilitar Respostas Automáticas da IA"
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={saveN8nSettings}
                  disabled={!n8nWebhookEnabled || !n8nWebhookUrl}
                >
                  Salvar Configurações
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={testN8nWebhook}
                  disabled={!n8nWebhookUrl || isTestingWebhook}
                  startIcon={isTestingWebhook ? <LinearProgress /> : <WebhookIcon />}
                >
                  {isTestingWebhook ? 'Testando...' : 'Testar Webhook'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>


      {/* Dialog: Nova Instância Evolution API */}
      <Dialog 
        open={createInstanceDialog} 
        onClose={() => setCreateInstanceDialog(false)} 
        maxWidth="md" 
        fullWidth
        BackdropProps={{
          sx: { backgroundColor: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(4px)' }
        }}
        PaperProps={{
          className: 'glass-blue-card',
          sx: {
            background: '#ffffff',
            border: '1px solid rgba(59, 130, 246, 0.15)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(59, 130, 246, 0.06)',
          }
        }}
      >
        <DialogTitle sx={{ color: '#000000', borderBottom: '1px solid rgba(59, 130, 246, 0.1)' }}>
          Conectar Novo Número WhatsApp
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Provedor</InputLabel>
                <Select
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value as 'evolution' | 'z-api')}
                  label="Provedor"
                >
                  <MenuItem value="evolution">Evolution API</MenuItem>
                  <MenuItem value="z-api">Z-API</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {selectedProvider === 'evolution' ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome da Instância"
                    value={newInstanceForm.name}
                    onChange={(e) => setNewInstanceForm({ ...newInstanceForm, name: e.target.value })}
                    required
                    placeholder="Ex: WhatsApp Empresa 1"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome Técnico"
                    value={newInstanceForm.instance_name}
                    onChange={(e) => setNewInstanceForm({ ...newInstanceForm, instance_name: e.target.value })}
                    required
                    helperText="Nome único para identificação técnica (ex: empresa1)"
                    placeholder="Ex: empresa1"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Limite Diário"
                    type="number"
                    value={newInstanceForm.daily_limit}
                    onChange={(e) => setNewInstanceForm({ ...newInstanceForm, daily_limit: parseInt(e.target.value) || 100 })}
                    required
                    helperText="Número máximo de mensagens por dia"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Prioridade"
                    type="number"
                    value={newInstanceForm.priority}
                    onChange={(e) => setNewInstanceForm({ ...newInstanceForm, priority: parseInt(e.target.value) || 1 })}
                    required
                    helperText="Prioridade de envio (1 = maior prioridade)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Tipo de Instância</InputLabel>
                    <Select
                      value={newInstanceForm.instance_type}
                      onChange={(e) => setNewInstanceForm({ ...newInstanceForm, instance_type: e.target.value as 'onboarding' | 'recovery' | 'both' })}
                      label="Tipo de Instância"
                    >
                      <MenuItem value="onboarding">Onboarding (Boas-vindas)</MenuItem>
                      <MenuItem value="recovery">Recuperação (Carrinho Abandonado)</MenuItem>
                      <MenuItem value="both">Ambos</MenuItem>
                    </Select>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                      Define para qual tipo de disparo esta instância será usada
                    </Typography>
                  </FormControl>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nome da Instância"
                    value={newZApiInstanceForm.name}
                    onChange={(e) => setNewZApiInstanceForm({ ...newZApiInstanceForm, name: e.target.value })}
                    required
                    placeholder="Ex: WhatsApp Empresa 1"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Instance ID"
                    value={newZApiInstanceForm.instance_id}
                    onChange={(e) => setNewZApiInstanceForm({ ...newZApiInstanceForm, instance_id: e.target.value })}
                    required
                    helperText="ID da instância na Z-API"
                    placeholder="Ex: instance123"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Token da Instância"
                    value={newZApiInstanceForm.instance_token}
                    onChange={(e) => setNewZApiInstanceForm({ ...newZApiInstanceForm, instance_token: e.target.value })}
                    required
                    type="password"
                    helperText="Token de autenticação da instância"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Client Token"
                    value={newZApiInstanceForm.client_token}
                    onChange={(e) => setNewZApiInstanceForm({ ...newZApiInstanceForm, client_token: e.target.value })}
                    required
                    type="password"
                    helperText="Token do cliente Z-API"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Limite Diário"
                    type="number"
                    value={newZApiInstanceForm.daily_limit}
                    onChange={(e) => setNewZApiInstanceForm({ ...newZApiInstanceForm, daily_limit: parseInt(e.target.value) || 100 })}
                    required
                    helperText="Número máximo de mensagens por dia"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newZApiInstanceForm.is_primary}
                        onChange={(e) => setNewZApiInstanceForm({ ...newZApiInstanceForm, is_primary: e.target.checked })}
                      />
                    }
                    label="Instância Primária"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateInstanceDialog(false)}>Cancelar</Button>
          <Button 
            onClick={selectedProvider === 'evolution' ? handleCreateEvolutionInstance : handleCreateZApiInstance} 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Criando...' : 'Criar Instância'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Editar Instância */}
      <Dialog 
        open={editInstanceDialog} 
        onClose={() => setEditInstanceDialog(false)} 
        maxWidth="md" 
        fullWidth
        BackdropProps={{
          sx: { backgroundColor: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(4px)' }
        }}
        PaperProps={{
          className: 'glass-blue-card',
          sx: {
            background: '#ffffff',
            border: '1px solid rgba(59, 130, 246, 0.15)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 4px 16px rgba(59, 130, 246, 0.06)',
          }
        }}
      >
        <DialogTitle sx={{ color: '#000000', borderBottom: '1px solid rgba(59, 130, 246, 0.1)' }}>
          Editar Instância WhatsApp
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome da Instância"
                value={editInstanceForm.name}
                onChange={(e) => setEditInstanceForm({ ...editInstanceForm, name: e.target.value })}
                required
                placeholder="Ex: WhatsApp Empresa 1"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Limite Diário"
                type="number"
                value={editInstanceForm.daily_limit}
                onChange={(e) => setEditInstanceForm({ ...editInstanceForm, daily_limit: parseInt(e.target.value) || 100 })}
                required
                helperText="Número máximo de mensagens por dia"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prioridade"
                type="number"
                value={editInstanceForm.priority}
                onChange={(e) => setEditInstanceForm({ ...editInstanceForm, priority: parseInt(e.target.value) || 1 })}
                required
                helperText="Prioridade de envio (1 = maior prioridade)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Tipo de Instância</InputLabel>
                <Select
                  value={editInstanceForm.instance_type}
                  onChange={(e) => setEditInstanceForm({ ...editInstanceForm, instance_type: e.target.value as 'onboarding' | 'recovery' | 'both' })}
                  label="Tipo de Instância"
                >
                  <MenuItem value="onboarding">Onboarding (Boas-vindas)</MenuItem>
                  <MenuItem value="recovery">Recuperação (Carrinho Abandonado)</MenuItem>
                  <MenuItem value="both">Ambos</MenuItem>
                </Select>
                <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                  Define para qual tipo de disparo esta instância será usada
                </Typography>
              </FormControl>
            </Grid>
            {editingInstance && (
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Nome Técnico:</strong> {editingInstance.instance_name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Telefone:</strong> {editingInstance.phone_number || 'Não conectado'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {editingInstance.is_connected ? 'Conectado' : 'Desconectado'}
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditInstanceDialog(false)}>Cancelar</Button>
          <Button onClick={handleUpdateInstance} variant="contained" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Nova Mensagem Automática */}
      <Dialog 
        open={messageDialogOpen} 
        onClose={() => setMessageDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          className: "glass-blue-card",
          sx: {
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          color: '#000000',
          borderBottom: '1px solid rgba(59, 130, 246, 0.1)'
        }}>
          {isEditing ? 'Editar Mensagem Automática' : 'Nova Mensagem Automática'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Informações Básicas */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: '#000000', fontWeight: 600 }}>
                Informações Básicas
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome da Mensagem"
                value={messageForm.name}
                onChange={(e) => setMessageForm({ ...messageForm, name: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      border: '1px solid rgba(59, 130, 246, 0.5)',
                    },
                    '&.Mui-focused': {
                      border: '1px solid #3b82f6',
                      boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': {
                      color: '#3b82f6',
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#000000',
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Delay (minutos)"
                type="number"
                value={messageForm.delay_minutes}
                onChange={(e) => setMessageForm({ ...messageForm, delay_minutes: parseInt(e.target.value) || 0 })}
                helperText="Tempo de espera antes de enviar"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      border: '1px solid rgba(59, 130, 246, 0.5)',
                    },
                    '&.Mui-focused': {
                      border: '1px solid #3b82f6',
                      boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': {
                      color: '#3b82f6',
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#000000',
                  },
                  '& .MuiFormHelperText-root': {
                    color: 'rgba(255, 255, 255, 0.6)',
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={messageForm.description}
                onChange={(e) => setMessageForm({ ...messageForm, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>

            {/* Seleção de Estágio e Pipeline */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Configuração do CRM</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Pipeline</InputLabel>
                <Select
                  value={messageForm.crm_pipeline_id}
                  onChange={(e) => setMessageForm({ ...messageForm, crm_pipeline_id: e.target.value })}
                >
                  {crmPipelines.map((pipeline) => (
                    <MenuItem key={pipeline.id} value={pipeline.id}>
                      {pipeline.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Estágio</InputLabel>
                <Select
                  value={messageForm.crm_stage_id}
                  onChange={(e) => setMessageForm({ ...messageForm, crm_stage_id: e.target.value })}
                  disabled={!messageForm.crm_pipeline_id}
                >
                  {crmStages
                    .filter(stage => stage.pipeline_id === messageForm.crm_pipeline_id)
                    .map((stage) => (
                      <MenuItem key={stage.id} value={stage.id}>
                        {stage.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Template da Mensagem */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Template da Mensagem</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Mensagem</InputLabel>
                <Select
                  value={messageForm.message_type}
                  onChange={(e) => setMessageForm({ ...messageForm, message_type: e.target.value as any })}
                >
                  <MenuItem value="text">Texto</MenuItem>
                  <MenuItem value="image">Imagem</MenuItem>
                  <MenuItem value="video">Vídeo</MenuItem>
                  <MenuItem value="document">Documento</MenuItem>
                  <MenuItem value="audio">Áudio</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {messageForm.message_type !== 'text' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="URL da Mídia"
                  value={messageForm.media_url}
                  onChange={(e) => setMessageForm({ ...messageForm, media_url: e.target.value })}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Template da Mensagem"
                value={messageForm.message_template}
                onChange={(e) => setMessageForm({ ...messageForm, message_template: e.target.value })}
                multiline
                rows={6}
                required
                helperText="Use {{contact_name}}, {{stage_name}}, {{pipeline_name}}, {{date}}, {{time}} para variáveis"
              />
            </Grid>

            {messageForm.message_type !== 'text' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Legenda da Mídia"
                  value={messageForm.media_caption}
                  onChange={(e) => setMessageForm({ ...messageForm, media_caption: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
            )}

            {/* Botões de Ação */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={generateMessagePreview}
                  disabled={!messageForm.message_template}
                >
                  Preview
                </Button>
                <FormControlLabel
                  control={
                    <Switch
                      checked={messageForm.is_active}
                      onChange={(e) => setMessageForm({ ...messageForm, is_active: e.target.checked })}
                    />
                  }
                  label="Mensagem Ativa"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMessageDialogOpen(false);
            resetMessageForm();
            setIsEditing(false);
            setSelectedItem(null);
          }}>
            Cancelar
          </Button>
          <Button 
            onClick={isEditing ? handleUpdateMessage : handleCreateMessage} 
            variant="contained"
            disabled={!messageForm.name || !messageForm.crm_stage_id || !messageForm.message_template}
          >
            {isEditing ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Preview da Mensagem */}
      <Dialog 
        open={previewDialog} 
        onClose={() => setPreviewDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          className: "glass-blue-card",
          sx: {
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle sx={{ color: '#000000', borderBottom: '1px solid rgba(59, 130, 246, 0.1)' }}>
          Preview da Mensagem
        </DialogTitle>
        <DialogContent>
          <Paper className="glass-blue-card" sx={{ 
            p: 2, 
          }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: '#000000' }}>
              {messagePreview}
            </Typography>
          </Paper>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Esta é uma prévia de como a mensagem será exibida para o contato.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Teste de Mensagem */}
      <Dialog 
        open={testMessageDialog} 
        onClose={() => setTestMessageDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          className: "glass-blue-card",
          sx: {
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle sx={{ color: '#000000', borderBottom: '1px solid rgba(59, 130, 246, 0.1)' }}>
          Testar Mensagem WhatsApp
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Número do WhatsApp"
                value={testMessage.phone}
                onChange={(e) => setTestMessage({ ...testMessage, phone: e.target.value })}
                placeholder="5511999999999"
                helperText="Número com código do país (ex: 5511999999999)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Conteúdo da Mensagem"
                value={testMessage.content}
                onChange={(e) => setTestMessage({ ...testMessage, content: e.target.value })}
                multiline
                rows={4}
                placeholder="Digite a mensagem de teste..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestMessageDialog(false)}>Cancelar</Button>
          <Button 
            onClick={handleTestMessage} 
            variant="contained"
            disabled={!testMessage.phone || !testMessage.content}
          >
            Enviar Teste
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Configurações do Sistema */}
      <Dialog 
        open={settingsDialogOpen} 
        onClose={() => setSettingsDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          className: "glass-blue-card",
          sx: {
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle sx={{ color: '#000000', borderBottom: '1px solid rgba(59, 130, 246, 0.1)' }}>
          Configurações do Sistema API não oficial
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Configurações Gerais */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Configurações Gerais</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={systemConfig?.auto_messages_enabled || false}
                    onChange={(e) => setSystemConfig({ 
                      ...systemConfig!, 
                      auto_messages_enabled: e.target.checked 
                    })}
                  />
                }
                label="Mensagens Automáticas Habilitadas"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Máximo de Mensagens por Contato/Dia"
                type="number"
                value={systemConfig?.max_daily_messages_per_contact || 3}
                onChange={(e) => setSystemConfig({ 
                  ...systemConfig!, 
                  max_daily_messages_per_contact: parseInt(e.target.value) 
                })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tentativas de Reenvio"
                type="number"
                value={systemConfig?.message_retry_attempts || 3}
                onChange={(e) => setSystemConfig({ 
                  ...systemConfig!, 
                  message_retry_attempts: parseInt(e.target.value) 
                })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Algoritmo de Distribuição</InputLabel>
                <Select
                  value={systemConfig?.distribution_algorithm || 'random'}
                  onChange={(e) => setSystemConfig({ 
                    ...systemConfig!, 
                    distribution_algorithm: e.target.value as any 
                  })}
                >
                  <MenuItem value="random">Aleatório</MenuItem>
                  <MenuItem value="round_robin">Rodízio</MenuItem>
                  <MenuItem value="priority">Prioridade</MenuItem>
                  <MenuItem value="load_balanced">Balanceamento de Carga</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Horário Comercial */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Horário Comercial</Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={systemConfig?.respect_business_hours || false}
                    onChange={(e) => setSystemConfig({ 
                      ...systemConfig!, 
                      respect_business_hours: e.target.checked 
                    })}
                  />
                }
                label="Respeitar Horário Comercial"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Início do Horário"
                type="time"
                value={systemConfig?.business_hours_start || '09:00'}
                onChange={(e) => setSystemConfig({ 
                  ...systemConfig!, 
                  business_hours_start: e.target.value 
                })}
                disabled={!systemConfig?.respect_business_hours}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Fim do Horário"
                type="time"
                value={systemConfig?.business_hours_end || '18:00'}
                onChange={(e) => setSystemConfig({ 
                  ...systemConfig!, 
                  business_hours_end: e.target.value 
                })}
                disabled={!systemConfig?.respect_business_hours}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Fuso Horário"
                value={systemConfig?.timezone || 'America/Sao_Paulo'}
                onChange={(e) => setSystemConfig({ 
                  ...systemConfig!, 
                  timezone: e.target.value 
                })}
              />
            </Grid>

            {/* Configurações da Fila */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Configurações da Fila</Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Intervalo de Processamento (ms)"
                type="number"
                value={systemConfig?.queue_processing_interval || 30000}
                onChange={(e) => setSystemConfig({ 
                  ...systemConfig!, 
                  queue_processing_interval: parseInt(e.target.value) 
                })}
                helperText="Intervalo entre processamentos da fila"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tamanho Máximo da Fila"
                type="number"
                value={systemConfig?.max_queue_size || 1000}
                onChange={(e) => setSystemConfig({ 
                  ...systemConfig!, 
                  max_queue_size: parseInt(e.target.value) 
                })}
                helperText="Máximo de mensagens na fila"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>Cancelar</Button>
          <Button 
            onClick={async () => {
              try {
                const API_BASE_URL = getApiUrl();
                await axios.put(`${API_BASE_URL}/whatsapp-auto/settings`, systemConfig);
                showSnackbar('Configurações salvas com sucesso!');
                setSettingsDialogOpen(false);
              } catch (err: any) {
                showSnackbar(err.response?.data?.error || 'Erro ao salvar configurações', 'error');
              }
            }} 
            variant="contained"
          >
            Salvar Configurações
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppAuto;

